// frontend/src/pages/situacoes/SituacoesListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import ProtectedPage from '../../components/common/ProtectedPage';
import { useAuth } from '../../contexts/AuthContext';

function SituacoesListPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('config.situacoes');

  const [situacoes, setSituacoes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchSituacoes = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/situacoes', { signal: controller.signal });
        setSituacoes(response.data?.data || []);
      } catch (err) {
        if (!controller.signal.aborted) setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchSituacoes();
    return () => controller.abort();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza de que deseja excluir esta situação?')) return;
    try {
      await api.delete(`/situacoes/${id}`);
      setSituacoes((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      if (err?.kind === 'permission') {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao excluir a situação.');
      }
    }
  };

  const filtered = [...situacoes]
    .sort((a, b) => a.nome.localeCompare(b.nome))
    .filter((s) => s.nome.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <LoadingSpinner text="Carregando situações..." />;

  if (error) {
    if (error.kind === 'permission') return <PermissionDeniedAlert />;
    return <AlertException error={error} fallback="Não foi possível carregar a lista de situações." />;
  }

  return (
    <ProtectedPage permission="config.situacoes" action="visualizar a lista de situações">
      <div className="content-header">
        <h1 className="content-title">Situações</h1>
        <p className="content-subtitle">Lista de situações cadastradas no sistema</p>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="input-group" style={{ maxWidth: '400px' }}>
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Link to="/painel/situacoes/novo" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Nova Situação
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((s) => (
                    <tr key={s.id}>
                      <td data-label="ID">{s.id}</td>
                      <td data-label="Nome">{s.nome}</td>
                      <td className="text-capitalize" data-label="Tipo">{s.tipo}</td>
                      <td data-label="Data de Criação">{String(s.created_at).split(' ')[0]}</td>
                      <td className="table-actions d-flex gap-2" data-label="Ações">
                        <Link
                          to={`/painel/situacoes/${s.id}/editar`}
                          className="btn btn-sm btn-outline-warning"
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </Link>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Excluir"
                          onClick={() => handleDelete(s.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">Nenhuma situação encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ProtectedPage>
  );
}

export default SituacoesListPage;
